// Browsing footprint: groups this device's viewed products by day (Today, Yesterday, dates) with per-item removal.
const STORAGE_KEY = 'hcr:recently-viewed';

class RecentlyViewed extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.products = new Map();
    this.addEventListener('command', (event) => {
      if (event.command === '--clear') this.clear();
      if (event.command === '--remove') this.remove(event.source?.dataset.handle);
    }, { signal: this.controller.signal });
    this.load();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  readEntries() {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return (Array.isArray(stored) ? stored : [])
        .map((item) => (typeof item === 'string' ? { h: item, t: 0 } : item))
        .filter((item) => item && typeof item.h === 'string');
    } catch (_) {
      return [];
    }
  }

  writeEntries(entries) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch (_) { /* Storage may be unavailable. */ }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(cents / 100);
  }

  dayKey(time) {
    const date = new Date(time);
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  }

  dayLabel(time) {
    if (!time) return this.dataset.labelEarlier;
    const today = new Date();
    const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    if (this.dayKey(time) === this.dayKey(today)) return this.dataset.labelToday;
    if (this.dayKey(time) === this.dayKey(yesterday)) return this.dataset.labelYesterday;
    const sameYear = new Date(time).getFullYear() === today.getFullYear();
    return new Intl.DateTimeFormat(this.dataset.locale || 'en', { month: 'long', day: 'numeric', weekday: 'short', ...(sameYear ? {} : { year: 'numeric' }) }).format(new Date(time));
  }

  async load() {
    const entries = this.readEntries();
    if (entries.length === 0) {
      this.showState('empty');
      return;
    }
    // Product JSON endpoint base comes from Liquid routes via data-products-url.
    const base = this.dataset.productsUrl;
    await Promise.all(entries.map(async ({ h }) => {
      try {
        const response = await fetch(`${base}${encodeURIComponent(h)}.js`, { signal: this.controller.signal });
        if (response.ok) this.products.set(h, await response.json());
      } catch (_) { /* Skip products that fail to load. */ }
    }));
    // Drop products that no longer exist so the footprint stays clean.
    const kept = entries.filter(({ h }) => this.products.has(h));
    if (kept.length !== entries.length) this.writeEntries(kept);
    this.render(kept);
  }

  render(entries) {
    const groupsRoot = this.querySelector('[data-recent-groups]');
    const groupTemplate = this.querySelector('template[data-recent-group]');
    const cardTemplate = this.querySelector('template[data-recent-card]');
    if (!groupsRoot || !groupTemplate || !cardTemplate || entries.length === 0) {
      groupsRoot?.replaceChildren();
      this.showState('empty');
      return;
    }

    const count = this.querySelector('[data-recent-count]');
    if (count) count.textContent = (entries.length === 1 ? this.dataset.countOne : this.dataset.countOther).replace('[count]', entries.length);

    const fragment = document.createDocumentFragment();
    let currentKey = null;
    let currentList = null;
    for (const { h, t } of entries) {
      const product = this.products.get(h);
      if (!product) continue;
      const key = t ? this.dayKey(t) : 'earlier';
      if (key !== currentKey) {
        const group = groupTemplate.content.cloneNode(true);
        group.querySelector('[data-recent-day]').textContent = this.dayLabel(t);
        currentList = group.querySelector('[data-recent-list]');
        fragment.append(group);
        currentKey = key;
      }
      currentList.append(this.buildCard(cardTemplate, product));
    }
    groupsRoot.replaceChildren(fragment);
    this.showState('list');
  }

  buildCard(template, product) {
    const card = template.content.cloneNode(true);
    const url = product.url || `${this.dataset.productsUrl}${product.handle}`;
    card.querySelectorAll('[data-recent-link]').forEach((link) => { link.href = url; });
    const image = card.querySelector('[data-recent-image]');
    if (image && product.featured_image) {
      const src = product.featured_image.startsWith('//') ? `https:${product.featured_image}` : product.featured_image;
      const sized = (width) => `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
      image.src = sized(600);
      image.srcset = `${sized(300)} 300w, ${sized(600)} 600w, ${sized(900)} 900w`;
      image.alt = product.title;
    } else if (image) {
      image.remove();
    }
    card.querySelector('[data-recent-title]').textContent = product.title;
    card.querySelector('[data-recent-price]').textContent = product.price_min === product.price_max
      ? this.formatMoney(product.price_min)
      : `${this.formatMoney(product.price_min)} – ${this.formatMoney(product.price_max)}`;
    if (!product.available) card.querySelector('[data-recent-soldout]')?.removeAttribute('hidden');
    const remove = card.querySelector('[data-recent-remove]');
    if (remove) {
      remove.dataset.handle = product.handle;
      remove.setAttribute('aria-label', this.dataset.removeLabel.replace('[title]', product.title));
    }
    return card;
  }

  remove(handle) {
    if (!handle) return;
    const entries = this.readEntries().filter(({ h }) => h !== handle);
    this.writeEntries(entries);
    const status = this.querySelector('[data-recent-status]');
    if (status) status.textContent = this.dataset.removedMessage;
    this.render(entries.filter(({ h }) => this.products.has(h)));
  }

  clear() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) { /* Storage may be unavailable. */ }
    this.render([]);
  }

  showState(state) {
    this.querySelectorAll('[data-recent-state]').forEach((element) => {
      element.hidden = element.dataset.recentState !== state;
    });
    this.removeAttribute('aria-busy');
  }
}

if (!customElements.get('s-recently-viewed')) customElements.define('s-recently-viewed', RecentlyViewed);
